import { forwardRef } from 'react';

import { type LBIcon } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { cn } from '@/lib/utils';

import {
  pageHeaderLeadingTunnel,
  pageHeaderTitleTunnel,
  pageHeaderTunnel,
} from './page-header-tunnel';

export const READING_WIDTH = 'max-w-4xl';

export const TOP_PADDING = 'pt-4 md:pt-10';

type ColumnOptions = {
  width?: 'reading' | 'full';
  top?: boolean;
};

export const column = ({
  width = 'reading',
  top = false,
}: ColumnOptions = {}) =>
  cn(
    'w-full px-6',
    width === 'reading' && cn('mx-auto', READING_WIDTH),
    top && TOP_PADDING
  );

export const Column = ({
  width,
  top,
  className,
  ...props
}: ColumnOptions & React.ComponentProps<typeof YStack>) => (
  <YStack className={cn(column({ width, top }), className)} {...props} />
);

export const Body = ({
  children,
  className,
  width = 'full',
  top = false,
}: {
  children: React.ReactNode;
  className?: string;
} & ColumnOptions) => (
  <ScrollMask className="h-full scrollbar-hide">
    <Column
      width={width}
      top={top}
      className={cn('min-h-full gap-5 pb-8', !top && 'pt-1', className)}
    >
      {children}
    </Column>
  </ScrollMask>
);

export const HeaderTitle = ({ children }: { children: React.ReactNode }) => (
  <pageHeaderTitleTunnel.In>{children}</pageHeaderTitleTunnel.In>
);

export const HeaderLeading = ({ children }: { children: React.ReactNode }) => (
  <pageHeaderLeadingTunnel.In>{children}</pageHeaderLeadingTunnel.In>
);

export const HeaderActions = ({ children }: { children: React.ReactNode }) => (
  <pageHeaderTunnel.In>
    <XStack className="min-w-0 items-center gap-3">{children}</XStack>
  </pageHeaderTunnel.In>
);

export const HEADER_CONTROL = 'h-9 shrink-0';

type HeaderButtonProps = {
  icon?: LBIcon;
} & React.ComponentProps<typeof Button>;

export const HeaderButton = forwardRef<HTMLButtonElement, HeaderButtonProps>(
  (
    { icon: Icon, variant = 'secondary', className, children, ...props },
    ref
  ) => (
    <Button
      ref={ref}
      variant={variant}
      className={cn(HEADER_CONTROL, 'gap-1.5', className)}
      {...props}
    >
      {Icon && <Icon className="size-3.5" />}
      {children}
    </Button>
  )
);
HeaderButton.displayName = 'Page.HeaderButton';
